import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { KpiCard } from '@/components/KpiCard';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Bot, Zap, CheckCircle2, DollarSign, Activity, ChevronRight, Sparkles } from 'lucide-react';
import { relativeTime, shortId } from '@/lib/utils';
import { SkeletonCard } from '@/components/Loading';

export default function Dashboard() {
  const nav = useNavigate();
  const { data: overview, isLoading } = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.get('/analytics/overview').then(r => r.data),
    refetchInterval: 15000,
  });
  const { data: agentsData } = useQuery({
    queryKey: ['agents-top'],
    queryFn: () => api.get('/agents').then(r => r.data),
  });

  const kpis = [
    { label: 'Active agents', value: overview ? `${overview.agents.active}/${overview.agents.total}` : '—', icon: Bot, testId: 'kpi-card-agents' },
    { label: 'Actions (24h)', value: overview ? overview.actions_24h : '—', icon: Zap, testId: 'kpi-card-actions' },
    { label: 'Verified real', value: overview ? `${Math.round((overview.real_rate || 0) * 100)}%` : '—', icon: CheckCircle2, testId: 'kpi-card-real-rate', hint: overview ? `${overview.real} real / ${overview.fake} fake` : '' },
    { label: 'Spend to date', value: overview ? `$${(overview.spend_usd || 0).toFixed(2)}` : '—', icon: DollarSign, testId: 'kpi-card-spend' },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHeader
        eyebrow="Operations"
        title="Dashboard"
        description="Live snapshot of your fleet. Updates every 15 seconds."
        actions={
          <Button onClick={() => nav('/agents?new=1')} data-testid="dashboard-new-agent-button">
            <Bot className="mr-2 h-4 w-4" /> New agent
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} h={132} />)
          : kpis.map((k) => <KpiCard key={k.label} {...k} />)
        }
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]" data-testid="recent-events-card">
          <div className="flex items-center justify-between border-b border-white/5 px-5 py-3">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-[hsl(var(--text-3))]" />
              <h3 className="text-sm font-medium">Recent events</h3>
            </div>
            <span className="text-xs text-[hsl(var(--text-3))]">Last 40</span>
          </div>
          <div className="max-h-[460px] overflow-auto">
            {!overview?.recent_events?.length ? (
              <EmptyState icon={Activity} title="No events yet" description="Events appear here as agents execute tasks." />
            ) : (
              overview.recent_events.map((e) => (
                <div key={e.id} className="group flex items-start gap-3 border-b border-white/5 px-5 py-3 last:border-0 hover:bg-[hsl(var(--surface-2))] transition-colors" data-testid={`event-${e.id}`}>
                  <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))]" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-mono text-xs text-[hsl(var(--text-3))]">{shortId(e.id)}</span>
                      <span className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[hsl(var(--text-2))]">{e.kind}</span>
                    </div>
                    {e.payload && Object.keys(e.payload).length > 0 && (
                      <div className="mt-1 text-xs font-mono text-[hsl(var(--text-3))] truncate">{JSON.stringify(e.payload)}</div>
                    )}
                  </div>
                  <span className="text-xs tabular text-[hsl(var(--text-3))]">{relativeTime(e.created_at)}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]" data-testid="top-agents-card">
          <div className="flex items-center justify-between border-b border-white/5 px-5 py-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[hsl(var(--text-3))]" />
              <h3 className="text-sm font-medium">Top agents</h3>
            </div>
          </div>
          <div className="divide-y divide-white/5">
            {(!agentsData?.items?.length) ? (
              <EmptyState icon={Bot} title="No agents yet" description="Create your first agent to get started." action={<Button size="sm" onClick={() => nav('/agents?new=1')} data-testid="empty-create-agent-button">Create agent</Button>} />
            ) : (
              agentsData.items.slice(0, 6).map((a) => (
                <button
                  key={a.id}
                  onClick={() => nav(`/agents/${a.id}`)}
                  className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-[hsl(var(--surface-2))] transition-colors"
                  data-testid={`top-agent-${a.id}`}
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[hsl(var(--primary))/0.12] text-[hsl(var(--primary))]">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{a.name}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-[hsl(var(--text-3))]">
                      <StatusDot state={a.status} />
                      <span className="font-mono">{a.total_actions} acts</span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[hsl(var(--text-3))] group-hover:text-[hsl(var(--text-1))]" />
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
